import { useState } from "react";
import {
  Box,
  Button,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { ClickAwayListener } from "@mui/base/ClickAwayListener";

import {
  AuthNotificationListCell,
  AuthNotificationsPaper,
  DocumentNameUnderline,
  DownloadButton,
  DownloadButtonBox,
  FaCaretDownIcon,
  FilePdfIcon,
} from "../style/style";
import { ReferralDetail } from "../../../models/Referral";
import { AuthNotificationsListResponse } from "../../../models/api/Referral";
import { formatDate } from "../../../utils/DateTime";
import { DateFormats } from "../../../constants/Date";
import fontWeight from "../../../styles/mui/fontWeight";
import { colors } from "../../../styles/colors";

type AuthNotificationsListType = {
  referral: ReferralDetail;
  authNotificationsListState: AuthNotificationsListResponse;
  postAuthNotifications: (referralId: number) => void;
  authNotificationsFileDownload: (blobId: number, fileName: string) => void;
};

const AuthNotificationsList: React.FC<AuthNotificationsListType> = ({
  referral,
  authNotificationsListState,
  postAuthNotifications,
  authNotificationsFileDownload,
}) => {
  const [openAuthNotificationsReferralId, setOpenAuthNotificationsReferralId] =
    useState<number | null>(null);

  const handleDownloadButton = (referralId: number) => {
    if (openAuthNotificationsReferralId === referralId) {
      setOpenAuthNotificationsReferralId(null);
    } else {
      setOpenAuthNotificationsReferralId(referralId);
      postAuthNotifications(referralId);
    }
  };

  const handleClickAway = () => {
    setOpenAuthNotificationsReferralId(null);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={DownloadButtonBox}>
        <Button
          sx={DownloadButton}
          onClick={() => handleDownloadButton(referral.referralId)}
        >
          Auth Notifications
          <FaCaretDownIcon />
        </Button>
        {openAuthNotificationsReferralId === referral.referralId ? (
          <Paper sx={AuthNotificationsPaper}>
            {authNotificationsListState.loading ? (
              <Typography sx={{ p: 1 }} role="status">
                Loading notifications...
              </Typography>
            ) : authNotificationsListState.response.length === 0 ? (
              <Typography sx={{ p: 1 }} role="status">
                The authorization packet is no longer available. Please contact
                support.
              </Typography>
            ) : (
              <Table size="small" aria-label="Authorization notifications">
                <TableBody>
                  {authNotificationsListState.response.map((notification) => (
                    <TableRow key={notification.documentId}>
                      <TableCell sx={AuthNotificationListCell}>
                        <FilePdfIcon />
                      </TableCell>
                      <TableCell sx={AuthNotificationListCell}>
                        <Link
                          variant="h5"
                          fontWeight={fontWeight[4]}
                          color={colors.fonts[18]}
                          onClick={() => {
                            authNotificationsFileDownload(
                              notification.blobId,
                              notification.originalDocumentName,
                            );
                            setOpenAuthNotificationsReferralId(null);
                          }}
                          sx={DocumentNameUnderline}
                        >
                          {notification.originalDocumentName}
                        </Link>
                      </TableCell>
                      <TableCell
                        sx={{
                          ...AuthNotificationListCell,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Typography component="span" variant="caption">
                          {formatDate(
                            notification.dateTimeReceived,
                            DateFormats.MM_DD_YYYY_hh_mm_ss_a,
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        ) : null}
      </Box>
    </ClickAwayListener>
  );
};

export default AuthNotificationsList;
